/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import { Form, FieldArray, FormikProvider, useFormik } from 'formik'
import Stack from '@mui/material/Stack'
import DeleteIcon from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import Tooltip from '@mui/material/Tooltip'

import CustomSnackbar from 'shared/components/CustomSnackbar'
import SelectField from 'shared/components/SelectField'
import InputField from 'shared/components/InputField'
import styled from '@emotion/styled'
import { Alert, Button } from '@mui/material'
import { useDeliveryTimesMutation, useDeliveryTimesQuery } from 'shared/store/useWholesalers'
import schema from './schema'

type TimeRecord = {
   time: string
   weekDay: 'MONDAY_FRIDAY' | 'SATURDAY'
}

type InitialFormValues = {
   times: TimeRecord[]
}

interface Props {
   wholesalerId: string
   closeModal: () => void
}

const initialValues: InitialFormValues = {
   times: [
      {
         time: '',
         weekDay: 'MONDAY_FRIDAY',
      },
   ],
}

const StyledDialogButton = styled(Button)(() => ({
   textTransform: 'uppercase',
   fontWeight: 'normal',
}))

export default function EditTimesModal({ wholesalerId, closeModal }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'WHOLESALERS'])
   const { data } = useDeliveryTimesQuery(wholesalerId)
   const { mutate, isError: isMutationError, error: mutationError, isSuccess } = useDeliveryTimesMutation()
   const [displaySnackbar, setDisplaySnackbar] = useState(false)
   const formik = useFormik<InitialFormValues>({
      initialValues,
      validationSchema: schema,
      onSubmit: handleSubmit,
   })

   async function handleSubmit(val: InitialFormValues) {
      const { times } = val
      const uuid = data?.uuid
      const deliveryTimes = times.map((item) => {
         const splitedTime = item.time.split(':')
         return {
            deliveryDays: item.weekDay,
            deliveryHour: {
               hour: Number(splitedTime[0]),
               minute: Number(splitedTime[1]),
            },
         }
      })

      if (uuid) {
         mutate(
            { uuid, deliveryTimes, wholesaleUuid: wholesalerId },
            {
               onSettled: () => setDisplaySnackbar(true),
            }
         )
      }
   }

   const handleRemoveElement = useCallback(
      (timeIndex: number) => {
         const newValues = formik.values.times.filter((_, index) => index !== timeIndex)
         handleSubmit({ times: newValues })
      },
      [formik.values]
   )

   useEffect(() => {
      if (data) {
         const newTimes: InitialFormValues = {
            times: data.deliveryTimes.map(({ deliveryDays, deliveryHour }) => {
               const parsedHours = deliveryHour.hour < 10 ? `0${deliveryHour.hour}` : deliveryHour.hour
               const parsedMinutes = deliveryHour.minute < 10 ? `0${deliveryHour.minute}` : deliveryHour.minute
               const time = `${parsedHours}:${parsedMinutes}`
               return {
                  time,
                  weekDay: deliveryDays,
               }
            }),
         }
         formik.setValues(newTimes)
      }
   }, [data])

   return (
      <div>
         <Typography variant="body1" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
            {t('WHOLESALERS:editTimesDialog')}
         </Typography>
         <div style={{ marginTop: 30 }}>
            <FormikProvider value={formik}>
               <Form>
                  <FieldArray name="times">
                     {({ push }) => (
                        <div>
                           {formik.values.times.length > 0 &&
                              formik.values.times.map((timeRecord, index) => (
                                 <Stack key={`timeRecord_${index}`} direction="column" spacing={5} sx={{ mb: 2 }}>
                                    <Stack direction={{ md: 'row', xs: 'column' }} spacing={2}>
                                       <InputField sx={{ maxWidth: 350 }} name={`times.${index}.time`} type="time" />
                                       <SelectField
                                          label={t('WHOLESALERS:deliveryDays')}
                                          name={`times.${index}.weekDay`}
                                          options={[
                                             {
                                                value: 'MONDAY_FRIDAY',
                                                name: t('WHOLESALERS:mondayFriday'),
                                             },
                                             {
                                                value: 'SATURDAY',
                                                name: t('WHOLESALERS:saturday'),
                                             },
                                          ]}
                                       />

                                       <Tooltip
                                          disableHoverListener={formik.values.times.length === 1}
                                          title={t('WHOLESALERS:deleteTooltip') || ''}
                                       >
                                          <IconButton
                                             sx={{ width: 50, height: 50, paddingTop: 2 }}
                                             onClick={() => handleRemoveElement(index)}
                                          >
                                             <DeleteIcon />
                                          </IconButton>
                                       </Tooltip>
                                    </Stack>
                                 </Stack>
                              ))}
                           <div style={{ width: '50%', textAlign: 'center', margin: '0 auto' }}>
                              <Tooltip title={t('WHOLESALERS:addTooltip') || ''}>
                                 <IconButton
                                    sx={{ border: '0.5px solid gray', marginTop: 2 }}
                                    onClick={() => push({ time: '', weekDay: '' })}
                                 >
                                    <AddIcon
                                       sx={{ fontSize: 36 }}
                                       color={formik.values.times.length >= 10 ? 'inherit' : 'primary'}
                                    />
                                 </IconButton>
                              </Tooltip>
                              {typeof formik.errors.times === 'string' && (
                                 <Alert severity="error" sx={{ my: 3 }}>
                                    {t(formik.errors.times, { amount: formik.values.times.length })}
                                 </Alert>
                              )}
                           </div>
                        </div>
                     )}
                  </FieldArray>
                  <StyledDialogButton>{t('TRANSLATION:cancel')}</StyledDialogButton>
                  <StyledDialogButton type="submit">{t('TRANSLATION:save')}</StyledDialogButton>
               </Form>
            </FormikProvider>
         </div>
         <CustomSnackbar
            open={displaySnackbar}
            alertDetails={{
               alertType: isSuccess ? 'success' : 'error',
               alertMessage: isMutationError ? t('TRANSLATION:failedSave') : t('TRANSLATION: successSave'),
            }}
            handleClose={() => setDisplaySnackbar(false)}
         />
      </div>
   )
}
